/** @jsx jsx */
import AppBar from '@material-ui/core/AppBar'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import * as Sentry from '@sentry/browser'
import AccountDonations from 'components/AccountDonations'
import AccountSubscriptions from 'components/AccountSubscriptions'
import {DonationPurpose, Metadata} from 'components/DonationBox/shared'
import FullScreenLoader from 'components/FullScreenLoader'
import Layout from 'components/Layout'
import redirectIfAnonymous from 'hoc/redirectIfAnonymous'
import useUser from 'hooks/useUser'
import env from 'lib/env'
import {useCallback, useEffect, useState} from 'react'
import useSWR from 'swr'
import {jsx} from 'theme-ui'

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  )
}

export type DonationItem = {
  amount: number
  charity_organization: DonationPurpose
  created_at: string
  updated_at: string
  currency: string
  donation_status: 'PAID'
  email: string
  first_name: string
  last_name: string
  metadata: Metadata
  payment_gateway: 'STRIPE' | 'PAYPAL'
  monthly: boolean
  subscription_id: string
}

type Props = {
  title: string
  emptyText: string
  errorTitle: string
  errorText: string
  WelcomeMessage: React.FC<{firstName: string; lastName: string; email: string}>
  signOutText: string
  donationsTitle: string
  totalText: string
  donationsTabTitle: string
  subscriptionsTabTitle: string
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const AccountContent: React.FC<Props> = ({
  title,
  emptyText,
  errorTitle,
  errorText,
  WelcomeMessage,
  signOutText,
  donationsTitle,
  totalText,
  donationsTabTitle,
  subscriptionsTabTitle,
}) => {
  const {logout} = useUser()
  const [selectedTab, setSelectedTab] = useState(0)
  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, newTab: number) => {
      setSelectedTab(newTab)
    },
    [],
  )

  const {data, error} = useSWR<{
    donations: DonationItem[]
  }>(`${env.GATSBY_DONATION_API}auth/one-time-donations?donation_status=PAID`)

  useEffect(() => {
    if (error) {
      Sentry.captureMessage(error)
    }
    if (data && !data.donations) {
      Sentry.captureMessage('Debug needed!:' + JSON.stringify(data))
    }
  }, [error, data])

  if (error || (data && !data.donations)) {
    return (
      <Layout
        sx={{
          maxWidth: 600,
          px: [15, 30],
          py: 30,
          mx: 'auto',
        }}
      >
        <h1>{errorTitle}</h1>
        <p>{errorText}</p>
      </Layout>
    )
  }
  if (!data) {
    return <FullScreenLoader />
  }
  const donations = data.donations
  const firstDonation = donations.length ? donations[0] : ({} as DonationItem)
  const {first_name, last_name, email} = firstDonation

  return (
    <Layout
      sx={{
        maxWidth: 1028,
        mx: 'auto',
        px: [15, , 0],
        pt: [50, , 100],
        pb: 50,
      }}
    >
      <h1>{title}</h1>
      <p sx={{fontSize: 16}}>
        {donations.length ? (
          <WelcomeMessage
            firstName={first_name}
            lastName={last_name}
            email={email}
          />
        ) : (
          emptyText
        )}

        <button
          onClick={logout}
          sx={{
            border: 'none',
            bg: 'transparent',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          ({signOutText})
        </button>
      </p>

      <h2
        sx={{
          mt: 40,
          mb: 30,
        }}
      >
        {donationsTitle}
      </h2>
      <AppBar position="static" color="transparent">
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          aria-label="Account tabs"
          variant="fullWidth"
        >
          <Tab label={donationsTabTitle} {...a11yProps(0)} />
          <Tab label={subscriptionsTabTitle} {...a11yProps(1)} />
        </Tabs>
      </AppBar>
      <TabPanel value={selectedTab} index={0}>
        <AccountDonations donations={donations} totalText={totalText} />
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        <AccountSubscriptions errorTitle={errorTitle} errorText={errorText} />
      </TabPanel>
    </Layout>
  )
}

export default redirectIfAnonymous(AccountContent)
